<template>
    <form-wrapper class="billing-form">
        <form-row align="bottom">
            <form-cell v-if="!isAlreadySubscribed" width="two-thirds">
                <common-page-header
                    :heading="'Your billing information'"
                    size="page-plain-header"
                    contentSpacing="medium"
                    :isCenter="false"
                ></common-page-header>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell>
                <stripe-component
                    ref="stripeComponent"
                    @payment-details-failed="paymentDetailsFailed"
                    @payment-details-saved="createSubscription"
                    @stripe-loaded="stripeLoaded"
                ></stripe-component>
            </form-cell>
        </form-row>
        <form-row v-if="!isAlreadySubscribed">
            <common-page-header
                :heading="$t('pages.billing.billing.billing_form.billing_address')"
                size="page-plain-header"
                contentSpacing="medium"
                :isCenter="false"
            ></common-page-header>
        </form-row>
        <form-row v-if="!isAlreadySubscribed">
            <form-cell width="half">
                <text-field
                    :placeholder="$t('pages.billing.billing.billing_form.street')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="address"
                    @change="change($event)"
                ></text-field>
            </form-cell>
            <form-cell v-if="!isAlreadySubscribed" width="half">
                <text-field
                    :placeholder="$t('pages.billing.billing.billing_form.city')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="city"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>
        <form-row v-if="!isAlreadySubscribed">
            <form-cell width="half">
                <text-field
                    :placeholder="$t('pages.billing.billing.billing_form.state')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="state"
                    @change="change($event)"
                ></text-field>
            </form-cell>
            <form-cell width="half">
                <text-field
                    :placeholder="$t('pages.billing.billing.billing_form.zip')"
                    :isRequired="false"
                    :formData="data"
                    valuePath="zip"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>
        <template #footer>
            <common-buttons align="center">
                <common-button
                    :isProcessing="isSending"
                    :label="$t('common.microcopy.continue')"
                    type="primary"
                    @click="triggerStripeSubmit"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import StripeComponent from 'components/page-elements/practice/billing/StripeComponent'
    import PracticesAPI from 'api/PracticesAPI'
    import { submitPlanCheckout } from 'services/planCheckoutHelpers'

    export default {
        mixins: [FormMixin],

        components: { StripeComponent },

        props: {
            monthlyPrice: { type: Number, reqired: true, default: null },
            yearlyPrice: { type: Number, reqired: true, default: null },
            isAlreadySubscribed: { type: Boolean, default: false },
            isAnnual: { type: Boolean, default: false },
            selectedPlan: { type: Object, default: null },
        },

        data() {
            return {
                isSending: true,
            }
        },

        methods: {
            triggerStripeSubmit() {
                this.isSending = true
                this.$refs.stripeComponent.submitPayment()
            },

            paymentDetailsFailed() {
                this.isSending = false
            },

            stripeLoaded() {
                this.isSending = false
            },

            billingAddressPayload() {
                return {
                    address: this.data.address,
                    city: this.data.city,
                    state: this.data.state,
                    zip: this.data.zip,
                }
            },

            async createSubscription(paymentMethodId) {
                if (this.isAlreadySubscribed) {
                    this.$emit('billing-address-saved')
                    return
                }

                const practice = this.$auth.getPractice()
                const request = this.selectedPlan
                    ? submitPlanCheckout({
                          practice,
                          plan: this.selectedPlan,
                          isAnnual: this.isAnnual,
                          paymentMethodId,
                          billingAddress: this.billingAddressPayload(),
                      })
                    : PracticesAPI.createSubscription({
                          billingAddress: this.billingAddressPayload(),
                          isAnnual: this.isAnnual,
                          paymentMethodId: paymentMethodId || undefined,
                      })

                return request
                    .then(() => {
                        this.$emit('subscription-created', this.selectedPlan)
                        this.$emit('billing-address-saved')
                    })
                    .catch(() => {
                        this.isSending = false
                    })
            },
        },

        mounted() {
            const practice = this.$auth.getPractice() || {}
            this.data.address = practice.address
            this.data.city = practice.city
            this.data.state = practice.state
            this.data.zip = practice.zip
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .billing-form {
        padding: 10px;
        background: $color--bg-dashboard;
        border-radius: 8px;
        margin-left: 10px;

        @include breakpoint(tablet) {
            margin-left: 0;
            margin-top: 10px;
        }

        .billing-form-type {
            display: flex;
            align-items: center;
            font-size: 14px;

            .form-field {
                margin: 0 10px;
            }
        }

        .billing-form--info {
            span {
                font-weight: 600;
                color: $color-black;
            }
        }
    }
</style>
